/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import {
  AnimatedAxis, // Any of these can be non-animated equivalents
  AnimatedLineSeries,
  XYChart,
  Tooltip,
  AreaSeries,
  Grid,
  AnimatedAreaStack,
  TooltipData,
} from "@visx/xychart";
import dayjs from "dayjs";
import { theme, Text } from "@nordcloud/gnui";
import { ReservationsAndCommitmentsCoverageServiceUsageType } from "~/generated/graphql";
import { BrickLoader, LoaderWrap } from "~/components";
import { ChartPlaceholder, Legend } from "~/components/Charts/components";
import { makeCustomTheme } from "~/components/Charts/utils";
import { dateFormat } from "~/constants";
import { useCurrency } from "~/hooks";
import { isEmpty, formatMoneyLabel } from "~/tools";
import {
  FONT_SIZE,
  TICK_LENGTH,
  CHART_MARGIN,
  VALUE_X_TICKS,
  VALUE_Y_TICKS,
} from "../constants";
import {
  TimePoint,
  TooltipProps,
  LegendProps,
  TooltipCallbackData,
} from "../types";
import { UsageTooltip } from "./UsageTooltip";

type Props<T> = {
  width: number;
  height: number;
  loading: boolean;
  data: T[] | null;
  highestCost: number;
  tooltipDetailCallback: (
    tooltipData: TooltipData<TimePoint>
  ) => TooltipCallbackData;
  serviceUsageType: ReservationsAndCommitmentsCoverageServiceUsageType;
  legendData: LegendProps[];
};

export function UsageAnalysisChart<T extends TimePoint>({
  width = 100,
  height = 100,
  loading = true,
  highestCost,
  data = [],
  tooltipDetailCallback,
  serviceUsageType,
  legendData,
}: Props<T>) {
  const { currency } = useCurrency();
  const chartTheme = makeCustomTheme(theme.colors.statusSuccess[1]);
  const adjustment = highestCost / 4;
  const hightestDomain = highestCost + adjustment;

  return (
    <LoaderWrap
      loading={loading}
      Component={BrickLoader}
      viewBox="0 0 10 4"
      backgroundColor={theme.color.background.ui01}
    >
      <div
        css={{
          position: "relative",
          marginBottom: theme.spacing.spacing04,
          overflowX: "hidden",
        }}
      >
        <ChartPlaceholder
          show={isEmpty(data ?? [])}
          width={width}
          height={height}
        />

        <XYChart
          height={height}
          width={width}
          xScale={{ type: "band" }}
          margin={CHART_MARGIN}
          theme={chartTheme}
          yScale={{
            type: "linear",
            domain: [0, hightestDomain],
          }}
        >
          <Grid
            key="grid"
            rows
            columns={false}
            numTicks={VALUE_Y_TICKS}
            strokeDasharray="1,3"
            strokeWidth={1}
          />

          <AnimatedAxis
            key="value-axis"
            orientation="left"
            numTicks={VALUE_Y_TICKS}
            tickLength={TICK_LENGTH}
            tickFormat={(n) => formatMoneyLabel(n, currency)}
            stroke={theme.color.border.focus}
            tickStroke={theme.color.border.focus}
            animationTrajectory="min"
            tickLabelProps={() => ({
              fill: theme.color.text.text02,
              fontSize: FONT_SIZE,
              fontFamily: theme.fonts.body,
            })}
          />

          <AnimatedAxis
            key="time-axis"
            orientation="bottom"
            tickLength={TICK_LENGTH}
            stroke={theme.color.border.focus}
            numTicks={VALUE_X_TICKS}
            tickFormat={(date) => dayjs(date).format(dateFormat.dayMonthShort)}
            // @ts-expect-error TODO: fix
            ticksComponent={() => Text}
            animationTrajectory="min"
            tickLabelProps={() => ({
              fill: theme.color.text.text02,
              fontSize: FONT_SIZE,
              textAnchor: "middle",
              fontFamily: theme.fonts.body,
            })}
          />

          <AreaSeries
            dataKey="totalCostArea"
            data={data ?? []}
            fill={theme.color.support.grey}
            color={theme.colors.black}
            opacity="0.4"
            xAccessor={({ date }: TimePoint) => date}
            yAccessor={({ totalCost }: TimePoint) => totalCost}
          />
          <AnimatedAreaStack renderLine={false}>
            <AreaSeries
              dataKey="reservedInstanceCostArea"
              data={data ?? []}
              fill={theme.color.support.cyan}
              color={theme.color.support.cyan}
              opacity="0.4"
              xAccessor={({ date }: TimePoint) => date}
              yAccessor={({ reservedInstanceCost }: TimePoint) =>
                reservedInstanceCost
              }
            />
            <AreaSeries
              dataKey="savingsPlanCostArea"
              data={data ?? []}
              fill={theme.color.support.yellow}
              color={theme.color.support.yellow}
              opacity="0.6"
              xAccessor={({ date }: TimePoint) => date}
              yAccessor={({ savingsPlanCost }: TimePoint) => savingsPlanCost}
            />
          </AnimatedAreaStack>

          <AnimatedLineSeries
            dataKey="totalCostLine"
            data={data ?? []}
            stroke={theme.colors.black}
            xAccessor={({ date }: TimePoint) => date}
            yAccessor={({ totalCost }: TimePoint) => totalCost}
          />

          <Tooltip<TimePoint>
            detectBounds
            showVerticalCrosshair
            snapTooltipToDatumX
            showSeriesGlyphs
            renderTooltip={({ tooltipData }) =>
              getTooltipDetail({
                tooltipData,
                tooltipDetailCallback,
                serviceUsageType,
              })
            }
          />
        </XYChart>
        <Legend inline data={legendData} />
      </div>
    </LoaderWrap>
  );
}

function getTooltipDetail({
  tooltipData,
  serviceUsageType,
  tooltipDetailCallback,
}: TooltipProps) {
  if (!tooltipData?.nearestDatum?.datum) {
    return;
  }
  const {
    date,
    totalCost,
    reservedInstanceCost,
    savingsPlanCost,
    onDemandCost,
    provider,
  } = tooltipDetailCallback(tooltipData);

  return (
    <UsageTooltip
      date={date}
      totalCost={totalCost ?? "0"}
      reservedInstanceCost={reservedInstanceCost}
      savingsPlanCost={savingsPlanCost}
      serviceUsageType={serviceUsageType}
      provider={provider}
      onDemandCost={onDemandCost}
    />
  );
}
